// packages/editor/src/studio/TemplateStudio.tsx
//
// Orchestration component for /studio/:cellPath. Composes:
//   - cell tree sidebar (passed as children to keep it host-app-controlled)
//   - tabbed Source / Preview / Diff content area
//   - right-rail SlotInspector (when cursor on slot)
//   - PublishModal (toggled by Publish button)
//
// Save flow: every change to source is staged in local state; on
// textarea blur, adapter.save fires; saveCounter bumps so DiffTab
// refreshes.

import { useCallback, useState } from 'react';
import type { SaveAdapter } from '../save-adapters/types';
import type { Fixture } from '../fixtures/apply-fixture';
import type { SlotConfig } from '../slot-grammar/slot-types';
import { SourceEditor } from './SourceEditor';
import { PreviewTab } from './PreviewTab';
import { DiffTab } from './DiffTab';
import { SlotInspector } from './SlotInspector';
import { PublishModal } from './PublishModal';

type Tab = 'source' | 'preview' | 'diff';

export interface TemplateStudioProps {
  adapter: SaveAdapter;
  cellPath: string;
  initialSource: string;
  fixture?: Fixture;
  /** Sidebar element (host app controls cell tree). */
  sidebar?: React.ReactNode;
}

export function TemplateStudio({ adapter, cellPath, initialSource, fixture, sidebar }: TemplateStudioProps) {
  const [tab, setTab] = useState<Tab>('source');
  const [source, setSource] = useState(initialSource);
  const [slotCfg, setSlotCfg] = useState<SlotConfig | null>(null);
  const [slotLineRange, setSlotLineRange] = useState<[number, number] | null>(null);
  const [saveCounter, setSaveCounter] = useState(0);
  const [publishOpen, setPublishOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleBlur = useCallback(async () => {
    try {
      await adapter.save(cellPath, source);
      setSaveError(null);
      setSaveCounter((n) => n + 1);
    } catch (e) {
      setSaveError((e as Error).message);
    }
  }, [adapter, cellPath, source]);

  const handleSlotApply = useCallback(
    (directiveString: string, _cfg: SlotConfig) => {
      if (!slotLineRange) return;
      const [start, end] = slotLineRange;
      const lines = source.split('\n');
      lines.splice(start, end - start + 1, directiveString);
      setSource(lines.join('\n'));
      setSlotCfg(null);
      setSlotLineRange(null);
    },
    [source, slotLineRange],
  );

  const handleSlotFocus = useCallback((cfg: SlotConfig | null) => {
    setSlotCfg(cfg);
    if (cfg) {
      // Find the line range of the slot directive — for source-mode
      // single-line directives, range is just [cursorLine, cursorLine].
      // Multi-line container slots come later (E2.5).
      const idx = source.split('\n').findIndex((ln) => /:::slot\[/.test(ln));
      if (idx >= 0) setSlotLineRange([idx, idx]);
    } else {
      setSlotLineRange(null);
    }
  }, [source]);

  return (
    <div data-testid="template-studio" style={{ display: 'flex', height: '100vh' }}>
      {sidebar}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <nav style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#fafafa' }}>
          {(['source', 'preview', 'diff'] as Tab[]).map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              style={{
                padding: '8px 16px',
                background: tab === t ? '#ffffff' : 'transparent',
                border: 'none',
                borderBottom: tab === t ? '2px solid #4a6fa5' : '2px solid transparent',
                cursor: 'pointer',
                fontFamily: 'system-ui',
                fontSize: 13,
                textTransform: 'capitalize',
              }}
            >
              {t}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button
            data-testid="publish-button"
            onClick={() => setPublishOpen(true)}
            style={{ margin: 4, padding: '6px 16px', background: '#4a6fa5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            Publish
          </button>
        </nav>

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {tab === 'source' && (
              <SourceEditor
                value={source}
                onChange={setSource}
                onBlur={handleBlur}
                onSlotFocus={handleSlotFocus}
              />
            )}
            {tab === 'preview' && <PreviewTab source={source} {...(fixture !== undefined ? { fixture } : {})} />}
            {tab === 'diff' && <DiffTab adapter={adapter} path={cellPath} saveCounter={saveCounter} />}
          </div>

          {tab === 'source' && slotCfg && (
            <SlotInspector
              initial={slotCfg}
              onApply={handleSlotApply}
              onClose={() => { setSlotCfg(null); setSlotLineRange(null); }}
            />
          )}
        </div>

        {saveError && (
          <div data-testid="save-error" style={{ padding: 8, background: '#fef2f2', color: '#7f1d1d', borderTop: '1px solid #fca5a5' }}>
            Save failed: {saveError}
          </div>
        )}
      </main>

      {publishOpen && (
        <PublishModal
          adapter={adapter}
          paths={[cellPath]}
          onClose={() => setPublishOpen(false)}
          onPublished={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}
